import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, catchError, forkJoin, map, of, switchMap, timeout } from 'rxjs';
import {
  CalendarClock,
  CircleDollarSign,
  FileText,
  Hospital,
  LucideAngularModule,
  NotebookPen,
  ShieldCheck,
  UserRound,
} from 'lucide-angular';

import { BannerComponent } from '../../../../../shared/components/banner/banner.component';
import {
  HealthEvent,
  HealthEventApi,
  HealthEventType,
  AppointmentApi,
} from '../../../../../core/interfaces/health-event.interface';
import { HealthEventService } from '../../../../../core/services/health-event.service';
import { AppointmentService } from '../../../../../core/services/appointment.service';
import { DocumentService } from '../../../../../core/services/document.service';
import { ModalService } from '../../../../../core/services/modal.service';
import { ToastService } from '../../../../../core/services/toast.service';
import { AddDocumentModalComponent } from '../../../../../shared/components/modal/add-document-modal';
import { PassportService } from '../../../../../core/services/passport.service';
import { SelectExistingDocumentsModalComponent } from '../../../../../shared/components/modal/select-existing-documents-modal/select-existing-documents-modal.component';

type EventSource = 'appointment' | 'health-event';
type EventDocument = {
  _id?: string;
  id?: string;
  filename?: string;
  url?: string;
  isFavorite?: boolean;
  documentType?: {
    _id?: string;
    id?: string;
    label?: string;
    value?: string;
  };
  createdAt?: string | Date;
};

@Component({
  selector: 'app-health-event-details',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, BannerComponent, LucideAngularModule],
  templateUrl: './health-event-details.component.html',
  styleUrl: './health-event-details.component.scss',
})
export class HealthEventDetailsComponent implements OnInit, OnDestroy {
  readonly CalendarClockIcon = CalendarClock;
  readonly UserRoundIcon = UserRound;
  readonly HospitalIcon = Hospital;
  readonly FileTextIcon = FileText;
  readonly NotebookPenIcon = NotebookPen;
  readonly CircleDollarSignIcon = CircleDollarSign;
  readonly ShieldCheckIcon = ShieldCheck;

  links: Array<{ label: string; href?: string }> = [
    { label: 'Votre passeport', href: undefined },
    { label: 'Suivi Santé', href: undefined },
    { label: "Détail de l'événement", href: undefined },
  ];

  event: HealthEvent | null = null;
  isLoading = true;
  error: string | null = null;
  private source: EventSource = 'health-event';
  private eventId = '';
  private activeTab = 'upcoming';
  private selectedCountryCode = '';
  private subscription = new Subscription();

  availableDocuments: EventDocument[] = [];
  attachedDocuments: EventDocument[] = [];
  documentsLoading = false;
  attachingDocuments = false;
  documentTypes: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private healthEventService: HealthEventService,
    private appointmentService: AppointmentService,
    private documentService: DocumentService,
    private modalService: ModalService,
    private toastService: ToastService,
    private passportService: PassportService
  ) { }

  ngOnInit(): void {
    this.selectedCountryCode = this.getSelectedCountryCode();
    this.links[0].href = this.selectedCountryCode
      ? `/${this.selectedCountryCode}/passport`
      : '/passport';
    this.links[1].href = this.selectedCountryCode
      ? `/${this.selectedCountryCode}/passport/health-tracking`
      : '/passport/health-tracking';

    this.subscription.add(
      this.route.paramMap
        .pipe(
          switchMap((params) => {
            this.eventId = params.get('eventId') || '';
            this.source =
              (this.route.snapshot.queryParamMap.get('source') as EventSource) ||
              'health-event';
            this.activeTab =
              this.route.snapshot.queryParamMap.get('tab') || 'upcoming';

            const navigation = this.router.getCurrentNavigation();
            const stateEvent = (navigation?.extras.state?.['event'] ||
              history.state?.event) as HealthEvent | undefined;

            if (stateEvent?.id === this.eventId) {
              return of(stateEvent);
            }

            return this.loadEventDetails(this.eventId, this.source);
          })
        )
        .subscribe({
          next: (event) => {
            this.event = event;
            this.isLoading = false;
            this.bootstrapDocumentsSection();
          },
          error: () => {
            this.error = "Impossible de charger les détails de l'événement";
            this.isLoading = false;
          },
        })
    );

    this.subscription.add(
      this.modalService.getEvents().subscribe((modalEvent) => {
        if (modalEvent?.type === 'existingDocumentsSelected') {
          const selectedIds: string[] = modalEvent?.data?.documentIds || [];
          if (selectedIds.length > 0) {
            this.attachDocumentsByIds(selectedIds);
          }
          return;
        }

        if (modalEvent?.type !== 'documentAdded' || !this.event?.passportNumber) {
          return;
        }

        const uploadedDocumentId = this.extractDocumentIdFromUploadResponse(
          modalEvent?.data?.response
        );

        this.loadAvailableDocuments(() => {
          if (uploadedDocumentId) {
            this.attachDocumentsByIds([uploadedDocumentId]);
          } else {
            this.toastService.success(
              'Document charge. Vous pouvez maintenant le rattacher a cet evenement.'
            );
          }
        });
      })
    );

    this.loadDocumentTypes();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private loadEventDetails(eventId: string, source: EventSource) {
    const appointmentRequest = this.appointmentService.getAppointmentDetails(eventId).pipe(
      timeout(10000),
      map((appointment) =>
        this.transformAppointmentToHealthEvent(appointment as AppointmentApi)
      ),
      catchError(() => of(null))
    );

    const healthEventRequest = this.healthEventService.getHealthEventById(eventId).pipe(
      timeout(10000),
      map((event) =>
        this.transformApiEventToHealthEvent(event as HealthEventApi)
      ),
      catchError(() => of(null))
    );

    return forkJoin({
      appointment: appointmentRequest,
      healthEvent: healthEventRequest,
    }).pipe(
      map(({ appointment, healthEvent }) => {
        const preferredEvent =
          source === 'appointment' ? appointment : healthEvent;
        const fallbackEvent =
          source === 'appointment' ? healthEvent : appointment;
        const mergedEvent = this.mergeEventDetails(preferredEvent, fallbackEvent);

        if (!mergedEvent) {
          throw new Error('DETAIL_LOAD_FAILED');
        }

        return mergedEvent;
      }),
      catchError(() => {
        throw new Error('DETAIL_LOAD_FAILED');
      })
    );
  }

  goBack(): void {
    const commands = this.selectedCountryCode
      ? [this.selectedCountryCode, 'passport', 'health-tracking']
      : ['passport', 'health-tracking'];

    this.router.navigate(commands, {
      queryParams: { tab: this.activeTab },
    });
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'completed':
        return 'Termine';
      case 'scheduled':
      case 'confirmed':
        return 'Confirme';
      case 'pending':
        return 'En attente';
      case 'pending_payment':
        return 'En attente de paiement';
      case 'cancelled':
        return 'Annule';
      case 'rejected':
        return 'Rejete';
      default:
        return 'Inconnu';
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'scheduled':
      case 'confirmed':
        return 'bg-blue-100 text-blue-700';
      case 'pending':
      case 'pending_payment':
        return 'bg-orange-100 text-orange-700';
      case 'cancelled':
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  getEventTypeLabel(type: HealthEventType | undefined): string {
    switch (type) {
      case HealthEventType.APPOINTMENT:
        return 'Rendez-vous';
      case HealthEventType.VACCINATION:
        return 'Vaccination';
      case HealthEventType.EXAMINATION:
        return 'Examen';
      case HealthEventType.TREATMENT:
        return 'Traitement';
      case HealthEventType.MEDICAL_SERVICE:
        return 'Prestation medicale';
      case HealthEventType.FINANCIAL_SERVICE:
        return 'Prestation financiere';
      default:
        return 'Evenement de sante';
    }
  }

  formatDate(date: Date | string | undefined): string {
    const validDate = this.toValidDate(date);
    if (!validDate) return 'Non renseigne';

    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(validDate);
  }

  formatTimeRange(event: HealthEvent): string {
    const startTime = event.appointment?.startTime;
    const endTime = event.appointment?.endTime;

    if (startTime && endTime) {
      return `${this.formatTimeString(startTime)} - ${this.formatTimeString(
        endTime
      )}`;
    }

    return this.formatTimeString(event.date);
  }

  formatAmount(amount?: number, currency?: string): string {
    if (amount == null) return 'Non renseigne';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  }

  private formatTimeString(value: string | Date | undefined): string {
    const validDate = this.toValidDate(value);
    if (!validDate) return 'Non renseigne';

    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(validDate);
  }

  private toValidDate(value: Date | string | undefined | null): Date | null {
    if (value == null) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  private getSelectedCountryCode(): string {
    if (typeof localStorage === 'undefined') return '';

    try {
      const stored = localStorage.getItem('defaultCountry');
      if (!stored) return '';
      const country = JSON.parse(stored);
      return country?.code?.toLowerCase() || '';
    } catch {
      return '';
    }
  }

  openSelectExistingDocumentsModal(): void {
    this.modalService.open({
      component: SelectExistingDocumentsModalComponent,
      title: 'Selectionner des documents existants',
      width: '640px',
      closable: true,
      data: {
        documents: this.availableDocuments,
        selectedIds: this.attachedDocuments
          .map((doc) => this.getDocumentId(doc))
          .filter((id): id is string => !!id),
      },
    });
  }

  openUploadDocumentModal(): void {
    if (!this.event?.passportNumber) {
      this.toastService.error("Impossible d'identifier le patient de cet evenement");
      return;
    }

    this.modalService.open({
      component: AddDocumentModalComponent,
      title: 'Ajouter un document',
      width: '500px',
      closable: true,
      data: {
        documentTypes: this.documentTypes,
        currentUser: this.buildCurrentUserForUpload(),
        subAccounts: [],
      },
    });
  }

  private attachDocumentsByIds(selectedIds: string[]): void {
    if (!this.event) return;

    if (selectedIds.length === 0) {
      this.toastService.warning('Selectionnez au moins un document');
      return;
    }

    const existingIds = this.attachedDocuments
      .map((doc) => this.getDocumentId(doc))
      .filter((id): id is string => !!id);

    const mergedIds = Array.from(new Set([...existingIds, ...selectedIds]));

    this.attachingDocuments = true;
    this.healthEventService
      .attachDocuments(this.event.id, mergedIds)
      .subscribe({
        next: () => {
          this.attachedDocuments = this.availableDocuments.filter((doc) => {
            const id = this.getDocumentId(doc);
            return !!id && mergedIds.includes(id);
          });
          this.attachingDocuments = false;
          this.modalService.closeAll();
          this.toastService.success('Documents rattaches avec succes');
        },
        error: (error) => {
          console.error('Erreur lors du rattachement des documents:', error);
          this.attachingDocuments = false;
          this.toastService.error(
            "Le backend n'a pas confirme le rattachement des documents"
          );
        },
      });
  }

  onViewDocument(doc: EventDocument): void {
    if (!doc.url) {
      this.toastService.error('URL du document manquante');
      return;
    }

    this.documentService.getPresignedUrl(doc.url).subscribe({
      next: (response) => {
        window.open(response.url, '_blank');
      },
      error: () => {
        this.toastService.error('Erreur lors du chargement du document');
      },
    });
  }

  private transformAppointmentToHealthEvent(
    appointment: AppointmentApi
  ): HealthEvent {
    const appointmentDate = appointment.startTime
      ? new Date(appointment.startTime)
      : new Date(appointment.date);

    let status:
      | 'completed'
      | 'scheduled'
      | 'cancelled'
      | 'pending'
      | 'confirmed'
      | 'rejected' = 'scheduled';

    switch (appointment.status) {
      case 'confirmed':
        status = 'confirmed';
        break;
      case 'pending':
        status = 'pending';
        break;
      case 'cancelled':
        status = 'cancelled';
        break;
      case 'completed':
        status = 'completed';
        break;
      case 'rejected':
        status = 'rejected';
        break;
      default:
        status =
          appointmentDate < new Date() ? 'completed' : 'scheduled';
    }

    return {
      id: appointment._id,
      type: HealthEventType.APPOINTMENT,
      title:
        appointment.title ||
        `Rendez-vous ${appointment.reason?.label || ''}`.trim(),
      description: appointment.description || appointment.notes || '',
      date: appointmentDate,
      status,
      location: appointment.structure?.name || appointment.structureName,
      doctor: appointment.doctor || appointment.professionalName,
      doctorPhoto: appointment.doctorPhoto,
      notes: appointment.notes || '',
      createdAt: new Date(appointment.createdAt),
      updatedAt: new Date(appointment.updatedAt),
      professionalName:
        appointment.professionalName || appointment.doctor || '',
      structureName:
        appointment.structure?.name || appointment.structureName || '',
      passportNumber: appointment.passportNumber,
      documents: appointment.documents || [],
      appointment,
      user: appointment.user,
    };
  }

  private transformApiEventToHealthEvent(apiEvent: HealthEventApi): HealthEvent {
    const eventDate = apiEvent.appointment?.date
      ? new Date(apiEvent.appointment.date)
      : new Date(apiEvent.date);

    const doctor = apiEvent.doctor || apiEvent.professionalName;
    const structureName = apiEvent.structure?.name || apiEvent.structureName;
    const statusValue = apiEvent.status || apiEvent.appointment?.status;

    let status:
      | 'completed'
      | 'scheduled'
      | 'cancelled'
      | 'pending'
      | 'confirmed'
      | 'rejected' = 'scheduled';

    switch (statusValue) {
      case 'confirmed':
        status = 'confirmed';
        break;
      case 'pending':
        status = 'pending';
        break;
      case 'cancelled':
        status = 'cancelled';
        break;
      case 'completed':
        status = 'completed';
        break;
      case 'rejected':
        status = 'rejected';
        break;
      default:
        status = eventDate < new Date() ? 'completed' : 'scheduled';
    }

    return {
      id: apiEvent._id,
      type: this.mapApiTypeToHealthEventType(apiEvent.type),
      title: apiEvent.title,
      description: apiEvent.description || '',
      date: eventDate,
      status,
      location: structureName,
      doctor,
      notes: apiEvent.notes || '',
      createdAt: new Date(apiEvent.createdAt),
      updatedAt: new Date(apiEvent.updatedAt),
      professionalName: apiEvent.professionalName || doctor,
      structureName,
      passportNumber: apiEvent.passportNumber,
      documents: apiEvent.documents || [],
      appointment: apiEvent.appointment || apiEvent,
      user: apiEvent.user,
    };
  }

  private mapApiTypeToHealthEventType(apiType: string): HealthEventType {
    switch ((apiType || '').toLowerCase()) {
      case 'appointment':
        return HealthEventType.APPOINTMENT;
      case 'vaccination':
        return HealthEventType.VACCINATION;
      case 'examination':
        return HealthEventType.EXAMINATION;
      case 'treatment':
        return HealthEventType.TREATMENT;
      case 'medical_service':
        return HealthEventType.MEDICAL_SERVICE;
      case 'financial_service':
        return HealthEventType.FINANCIAL_SERVICE;
      default:
        return HealthEventType.OTHER;
    }
  }

  private bootstrapDocumentsSection(): void {
    this.attachedDocuments = this.extractAttachedDocuments(this.event);
    this.loadAvailableDocuments();
  }

  private loadAvailableDocuments(onDone?: () => void): void {
    if (!this.event?.passportNumber) return;

    this.documentsLoading = true;
    this.documentService
      .getDocumentsByPassportNumber(this.event.passportNumber, null, {
        page: 1,
        limit: 50,
      })
      .subscribe({
        next: (response) => {
          this.availableDocuments = response?.documents || response?.data || [];
          this.documentsLoading = false;
          if (onDone) onDone();
        },
        error: (error) => {
          console.error('Erreur lors du chargement des documents:', error);
          this.availableDocuments = [];
          this.documentsLoading = false;
          if (onDone) onDone();
        },
      });
  }

  private loadDocumentTypes(): void {
    this.passportService.getAllDocumentTypes().subscribe({
      next: (response) => {
        this.documentTypes = response?.data || response || [];
      },
      error: () => {
        this.documentTypes = [];
      },
    });
  }

  private extractAttachedDocuments(event: HealthEvent | null): EventDocument[] {
    if (!event) return [];
    this.healthEventService.getAttachedDocuments(event.id).subscribe({
      next: (response) => {
        this.attachedDocuments = response || [];
        this.attachingDocuments = false;
      },
      error: (error) => {
        this.attachingDocuments = false;
      },
    });
    return event.documents || [];

  }

  private mergeEventDetails(
    primaryEvent: HealthEvent | null,
    fallbackEvent: HealthEvent | null
  ): HealthEvent | null {
    if (!primaryEvent && !fallbackEvent) {
      return null;
    }

    if (!primaryEvent) {
      return fallbackEvent;
    }

    if (!fallbackEvent) {
      return primaryEvent;
    }

    const primaryDocuments = this.extractAttachedDocuments(primaryEvent);
    const fallbackDocuments = this.extractAttachedDocuments(fallbackEvent);
    const mergedDocuments =
      primaryDocuments.length > 0 ? primaryDocuments : fallbackDocuments;

    const mergedAppointment = {
      ...(fallbackEvent.appointment || {}),
      ...(primaryEvent.appointment || {}),
    };

    if (mergedDocuments.length > 0) {
      mergedAppointment.documents = mergedDocuments;
    }

    return {
      ...fallbackEvent,
      ...primaryEvent,
      documents: mergedDocuments,
      appointment:
        Object.keys(mergedAppointment).length > 0
          ? mergedAppointment
          : primaryEvent.appointment || fallbackEvent.appointment,
      user: primaryEvent.user || fallbackEvent.user,
    };
  }

  private getDocumentId(doc: EventDocument): string | null {
    return doc._id || doc.id || null;
  }

  private extractDocumentIdFromUploadResponse(response: any): string | null {
    return (
      response?._id ||
      response?.id ||
      response?.document?._id ||
      response?.document?.id ||
      response?.data?._id ||
      response?.data?.id ||
      null
    );
  }

  private buildCurrentUserForUpload(): any {
    return {
      firstName: this.event?.user?.firstName || 'Patient',
      lastName: this.event?.user?.lastName || '',
      passportNumber: this.event?.passportNumber || this.event?.user?.passportNumber || '',
    };
  }
}
